<template>
  <ModalBase :show-modal="true" :auto-h="true" :closeable="!(!!showNotifyCheck)" narrow @close-modal="close" class="gk-modal-copy-invoice">
    <template v-slot:titlebar>
      <TitleBar variant-class="title-modal">
        <template v-slot:pretitle>
          <div>
            {{ $gettext('Change Status') }}
          </div>
        </template>
        <template v-slot:title>
          {{ $gettext('Quotation') }} {{ quotation.number }}
        </template>
      </TitleBar>
    </template>
    <template v-slot:maincontent>
      <div v-if="!showNotifyCheck">
        <div class="status-box-wrap">
          <div class="status-box">
            <div class="gk-modal-label" v-translate>Current status</div>
            <div class="current-status-box" :style="badgeColor">{{ quotation.status }}</div>
          </div>
          <div class="status-box--arrow">
            <font-awesome-icon icon="fa-solid fa-arrow-right" class="mr-0" />
          </div>
          <div class="status-box">
            <div class="gk-modal-label" v-translate>New status</div>
            <b-form-select v-model="selectedStatus" :options="statusOptions" />
          </div>
        </div>
        <div v-if="selectedStatus && (selectedStatus === 'accepted' || selectedStatus === 'invoiced')" class="gk-tip gk-tip--warning">
          {{ $gettext('After changing the status to accepted or invoiced, you can no longer edit this quotation.') }}
        </div>
        <div v-if="selectedStatus && (quotation.staus === 'draft' && selectedStatus === 'shared')" class="gk-tip gk-tip--warning">
          {{ $gettext('If you change the status to shared, you still need to send the quotation in order to notify your customer.') }}
        </div>
      </div>
      <div v-if="showNotifyCheck">
        {{ $gettext('Your customer is not yet informed that you updated the status of your quotation. Would you like to send a notification?') }}
      </div>
    </template>
    <template v-slot:actions>
      <GekkoButton 
        v-if="!showNotifyCheck"
        icon="fa-solid fa-arrows-rotate"
        :text="$gettext('Change status')"
        @click="initSave()"
        class="position-relative"
        :loading="isWaitingForResponse"
      />
      <div v-if="showNotifyCheck" class="d-flex justify-content-end align-items-center">
        <GekkoButton 
          @click="save(false)"
          :text="$gettext('Don\'t notify')" 
          variant="link"
          class="position-relative text-primary mr-2"
          size="sm"
          :loading="isWaitingForResponse"
        />
        <GekkoButton 
          @click="save(true)"
          icon="fa-solid fa-paper-plane"
          :text="$gettext('Send notification')"
          class="position-relative"
          :loading="isWaitingForResponse"
        />
      </div>
    </template>
</ModalBase>
</template>

<script>
import { mapGetters, mapMutations } from "vuex";
import ModalBase from "@/components/base_components/ModalBase"
import TitleBar from "@/components/TitleBar"
import GekkoButton from "@/components/GekkoButton"
import QuotationService from "@/store/services/quotation.service.js"

export default {
  name: 'ChangeQuotationStatus',
  components: {
    ModalBase,
    TitleBar,
    GekkoButton
  },
  props: {
    quotation: {
      type: Object,
      default: () => {}
    }
  },
  data () {
    return {
      selectedStatus: null,
      showNotifyCheck: false
    }
  },
  computed: {
      ...mapGetters({
        isWaitingForResponse: 'main/isWaitingForResponse'
      }),
      badgeColor() {
          if (this.quotation.status === "draft")
              return "background-color: #CCD8E3; color: #1C2833"
          else if (this.quotation.status === "shared")
              return "background-color: #DAEEF6; color: #1E5C76"
          else if (this.quotation.status === "rejected")
              return "background-color: #F3E2E7; color: #411B26"
          return "background-color: #E1EFD3; color: #3B571F"
      },
      statusOptions() {
          let options = [
              { value: null, text: this.$gettext('Please select an option') }
          ]

          if (this.quotation.status === 'accepted') {
              options.push({value: 'invoiced', text: this.$gettext('Invoiced')})
          } else if (this.quotation.status === 'invoiced') {
              options.push({value: 'accepted', text: this.$gettext('Accepted')})
          } else if (this.quotation.status === 'shared') {
              options.push({value: 'invoiced', text: this.$gettext('Invoiced')})
              options.push({value: 'accepted', text: this.$gettext('Accepted')})
              options.push({value: 'rejected', text: this.$gettext('Rejected')})
          } else if (this.quotation.status === 'rejected') {
              options.push({value: 'invoiced', text: this.$gettext('Invoiced')})
              options.push({value: 'accepted', text: this.$gettext('Accepted')})
              options.push({value: 'shared', text: this.$gettext('Shared')})
          } else {
              options.push({value: 'invoiced', text: this.$gettext('Invoiced')})
              options.push({value: 'accepted', text: this.$gettext('Accepted')})
              options.push({value: 'shared', text: this.$gettext('Shared')})
              options.push({value: 'rejected', text: this.$gettext('Rejected')})
          }

          return options
      }
  },
  mounted () {
    document.body.classList.add('modal-on');
  },
  methods: {
    ...mapMutations({
      setWaitingForResponse: 'main/SET_WAITING_FOR_RESPONSE'
    }),
    close () {
      this.$emit('close');
      document.body.classList.remove('modal-on');
    },
    initSave () {
      if (this.selectedStatus === 'accepted' || this.selectedStatus === 'invoiced') {
        this.showNotifyCheck = true;
      } else {
        this.save()
      }
    },
    save (notify = false) {
      let quotationToUpdate = JSON.parse(JSON.stringify(this.quotation));

      if (!this.selectedStatus) {
        this.$store.commit('setAlert', {
          title: this.$gettext("Error"),
          text: this.$gettext("You must select a status"),
          variant: "danger",
          duration: 4
        })
        return
      }

      this.setWaitingForResponse(true)

      quotationToUpdate.status = this.selectedStatus;
      
      QuotationService.changeStatus(this.quotation.id, quotationToUpdate, notify).then(() => {
        this.$emit("status-change");
        this.close()
      }).catch((error) => {
        this.$store.commit('setAlert', {
          title: this.$gettext("Error"),
          text: this.$gettext("You cannot change the status of this quotation"),
          variant: "danger",
          duration: 4
        });
      }).finally(() => {
        this.setWaitingForResponse(false)
      });
    },
  }
}
</script>

<style scoped lang="scss">
.status-box-wrap {
    display: flex;
    flex-direction: row;
    gap: 1rem;
    padding: 1rem 0;
    .status-box {
        display: flex;
        flex-direction: column;
        width: 100%;
        &--arrow { align-self: flex-end; font-size: 1.5rem; }
        .gk-modal-label {
            margin-bottom: 0.25rem;
        }
        .current-status-box {
            display: flex;
            justify-content: center;
            align-items: center;
            height: 100%;
            border-radius: $radiusMd;
            font-size: 20px;
        }
    }
}




</style>